import { CSS } from '@stitches/react';

import Badge from '@/components/Badge';
import { NoshowAlertStatus } from '@/types/noshowAlerts';

const STATUS_CONFIG: Record<
  NoshowAlertStatus,
  { label: string; status: string; css?: CSS }
> = {
  pending: {
    label: 'Pending',
    status: 'neutral',
    css: { $$bgColor: '#EAF1FF', $$color: '#4785FF' },
  },
  sms_sent: { label: 'SMS Sent', status: 'theme' },
  reminder_sent: { label: 'Reminder Sent', status: 'theme' },
  confirmed_present: { label: 'Present', status: 'success' },
  confirmed_noshow: {
    label: 'No-Show',
    status: 'danger',
    css: { $$bgColor: '#FFDFD8', $$color: '#CF040C' },
  },
  backfill_requested: { label: 'Backfill Needed', status: 'warning' },
  awaiting_backfill: { label: 'Awaiting Backfill', status: 'theme' },
  backfill_declined: { label: 'Backfill Declined', status: 'neutral' },
  escalated: {
    label: 'No Response',
    status: 'neutral',
    css: { $$bgColor: '#FFC9ED', $$color: '#73113D' },
  },
  multi_worker_flagged: {
    label: 'Multi-Worker',
    status: 'neutral',
    css: { $$bgColor: '#FFF4D8', $$color: '#AA7A00' },
  },
  resolved: { label: 'Resolved', status: 'neutral' },
};

interface StatusBadgeProps {
  status: NoshowAlertStatus;
}

const StatusBadge = ({ status }: StatusBadgeProps) => {
  const config = STATUS_CONFIG[status] || {
    label: status,
    status: 'neutral',
  };
  return <Badge label={config.label} status={config.status} css={config.css} />;
};

const PRESENCE_CONFIG: Record<
  string,
  { label: string; status: string; css?: CSS }
> = {
  yes: { label: 'Present', status: 'success' },
  no: {
    label: 'No-Show',
    status: 'danger',
    css: { $$bgColor: '#FFDFD8', $$color: '#CF040C' },
  },
};

const BACKFILL_CONFIG: Record<
  string,
  { label: string; status: string; css?: CSS }
> = {
  yes: {
    label: 'Backfill Requested',
    status: 'neutral',
    css: { $$bgColor: '#CF040C', $$color: '#FFFFFF' },
  },
  no: {
    label: 'Declined — Follow Up',
    status: 'neutral',
    css: { $$bgColor: '#DEA000', $$color: '#FFFFFF' },
  },
};

interface PresenceStatusProps {
  status: NoshowAlertStatus;
  presenceResponse: string | null;
}

const PresenceStatus = ({ status, presenceResponse }: PresenceStatusProps) => {
  if (presenceResponse && PRESENCE_CONFIG[presenceResponse]) {
    const config = PRESENCE_CONFIG[presenceResponse];
    return (
      <Badge label={config.label} status={config.status} css={config.css} />
    );
  }

  if (status === 'escalated') {
    return (
      <Badge
        label="No Response"
        status="neutral"
        css={{ $$bgColor: '#FFC9ED', $$color: '#73113D' }}
      />
    );
  }
  if (status === 'multi_worker_flagged') {
    return (
      <Badge
        label="Multi-Worker"
        status="neutral"
        css={{ $$bgColor: '#FFF4D8', $$color: '#AA7A00' }}
      />
    );
  }
  if (['sms_sent', 'reminder_sent'].includes(status)) {
    return <Badge label="Awaiting" status="theme" />;
  }
  if (status === 'pending') {
    return (
      <Badge
        label="Pending"
        status="neutral"
        css={{ $$bgColor: '#EAF1FF', $$color: '#4785FF' }}
      />
    );
  }

  return <Badge label="-" status="neutral" />;
};

interface BackfillStatusProps {
  status: NoshowAlertStatus;
  backfillResponse: string | null;
}

const BackfillStatus = ({ status, backfillResponse }: BackfillStatusProps) => {
  if (backfillResponse && BACKFILL_CONFIG[backfillResponse]) {
    const config = BACKFILL_CONFIG[backfillResponse];
    return (
      <Badge label={config.label} status={config.status} css={config.css} />
    );
  }

  if (status === 'awaiting_backfill') {
    return <Badge label="Awaiting" status="theme" />;
  }

  return <Badge label="-" status="neutral" />;
};

export default StatusBadge;
export { PresenceStatus, BackfillStatus };
